"use client";

import { useReducedMotion } from "framer-motion";
import type * as React from "react";
import { useEffect, useRef } from "react";

import { cn } from "../../lib/cn";

/**
 * LineBlob — the "pencil-line" brand blob.
 *
 * A SMOOTH organic blob OUTLINE (orange stroke, no fill) drawn as an SVG path
 * through Catmull-Rom-smoothed control points. It wobbles continuously and, on
 * the `hero` size, DEFORMS toward the pointer (the edge facing the cursor
 * bulges out) — the way the old WebGL metaballs reacted. A minimal, stylised
 * alternative to `AndoBrain`/`LoginBlob`, sharing the same `size` API so it
 * drops into the login hero, chat streaming indicator, project anchors and the
 * marketing landing without touching consumers.
 *
 * rAF-driven (no CSS keyframes) so it behaves identically in apps/client AND
 * apps/web. Reduced motion → a still organic silhouette.
 */
export type LineBlobSize = "hero" | "mini";

export interface LineBlobProps {
  size?: LineBlobSize;
  className?: string;
  /** Override the stroke width (px, non-scaling). Defaults: hero 3.5, mini 1.3. */
  strokeWidth?: number;
}

const N = 7; // control points — enough for an organic, never-circular blob
const CX = 100;
const CY = 100;
const BASE_R = 58; // resting radius (viewBox 200, leaves room for bulge + stroke)
const WOBBLE = 8; // idle organic breathing amplitude
const CURSOR_PULL = 26; // how far the edge bulges toward the pointer

interface Cursor {
  x: number; // -1..1 (offset from centre / half-size)
  y: number;
  active: boolean;
}

/** Catmull-Rom → cubic-bezier closed path through the points (smooth, no kinks). */
function smoothClosedPath(pts: ReadonlyArray<readonly [number, number]>): string {
  const n = pts.length;
  const at = (i: number): readonly [number, number] => pts[((i % n) + n) % n] as [number, number];
  const start = at(0);
  let d = `M ${start[0].toFixed(2)} ${start[1].toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return `${d} Z`;
}

/** Blob silhouette at time ``t`` (ms), deformed toward the (normalised) cursor. */
function blobPath(t: number, cursor: Cursor): string {
  const cursorAngle = Math.atan2(cursor.y, cursor.x);
  const cursorMag = Math.min(1, Math.hypot(cursor.x, cursor.y));
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    // Two out-of-phase sines per point → organic, non-repeating wobble.
    const wob =
      Math.sin(t * 0.0007 + i * 1.7) * WOBBLE + Math.cos(t * 0.0011 + i * 2.6) * (WOBBLE * 0.55);
    // Bulge the edge facing the cursor (0 when opposite, max when aligned).
    const bulge = cursor.active
      ? Math.max(0, Math.cos(a - cursorAngle)) * cursorMag * CURSOR_PULL
      : 0;
    const r = BASE_R + wob + bulge;
    pts.push([CX + r * Math.cos(a), CY + r * Math.sin(a)]);
  }
  return smoothClosedPath(pts);
}

export const LineBlob: React.FC<LineBlobProps> = ({ size = "hero", className, strokeWidth }) => {
  const reduced = useReducedMotion() ?? false;
  const hero = size === "hero";
  const interactive = hero && !reduced;
  const stroke = strokeWidth ?? (hero ? 3.5 : 1.3);

  const wrapRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  // Raw last pointer position (window-global → the blob reacts from far away,
  // not only once the cursor is over it).
  const rawCursor = useRef<{ x: number; y: number; seen: boolean }>({ x: 0, y: 0, seen: false });
  // Smoothed pull vector — direction toward the cursor, magnitude 0..1 by
  // proximity. Eased every frame so the deformation never snaps.
  const pull = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;
    if (reduced) {
      path.setAttribute("d", blobPath(0, { x: 0, y: 0, active: false }));
      return;
    }

    const onWinMove = (e: PointerEvent): void => {
      rawCursor.current = { x: e.clientX, y: e.clientY, seen: true };
    };
    if (interactive) window.addEventListener("pointermove", onWinMove, { passive: true });

    let raf = 0;
    const loop = (t: number): void => {
      // Target pull toward the cursor, scaled by proximity with a smoothstep
      // falloff: the blob ALREADY leans gently from afar and grows smoothly as
      // the pointer approaches — no on/off snap when entering its box.
      let tx = 0;
      let ty = 0;
      const el = wrapRef.current;
      if (interactive && rawCursor.current.seen && el) {
        const r = el.getBoundingClientRect();
        const dx = rawCursor.current.x - (r.left + r.width / 2);
        const dy = rawCursor.current.y - (r.top + r.height / 2);
        const dist = Math.hypot(dx, dy) || 1;
        const reach = r.width * 1.8;
        let m = Math.max(0, 1 - dist / reach);
        m = m * m * (3 - 2 * m); // smoothstep
        tx = (dx / dist) * m;
        ty = (dy / dist) * m;
      }
      // Ease toward the target each frame → smooth in AND out, never abrupt.
      pull.current.x += (tx - pull.current.x) * 0.07;
      pull.current.y += (ty - pull.current.y) * 0.07;
      const mag = Math.hypot(pull.current.x, pull.current.y);
      path.setAttribute(
        "d",
        blobPath(t, { x: pull.current.x, y: pull.current.y, active: mag > 0.002 }),
      );
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      if (interactive) window.removeEventListener("pointermove", onWinMove);
    };
  }, [reduced, interactive]);

  return (
    <div
      ref={wrapRef}
      aria-hidden
      className={cn(
        "grid place-items-center",
        hero ? "mx-auto aspect-square w-full max-w-[480px]" : "aspect-square h-full w-full",
        className,
      )}
    >
      <svg
        aria-hidden
        role="presentation"
        viewBox="0 0 200 200"
        className={cn("overflow-visible", hero ? "h-[98%] w-[98%]" : "h-[84%] w-[84%]")}
      >
        <path
          ref={pathRef}
          fill="none"
          stroke="#E5651A"
          strokeWidth={stroke}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};
LineBlob.displayName = "LineBlob";
